import { Body, Headline } from '@shipt/design-system-typography'
import styled from 'styled-components'
import { type AdShowcaseData } from '@/features/cms/components/CMS/types'
import { Image } from '@/features/shared/elements/Image/NextImage'
import { NextLink } from '@/features/shared/elements/Link'
import { screenSizes } from '@shipt/design-system-themes'
import { IconButton } from '@shipt/design-system-buttons'
import { ArrowRightIcon } from '@shipt/design-system-icons'
import { Column, Row } from '@shipt/design-system-layouts'
import { useRenderShelfProducts } from '@/features/shop/components/Shelf/hooks/useRenderShelfProducts'
import { parsePromotionCategoryId } from '@/features/cms/components/CMS/utils/parse'
import { DISPLAY_FORMAT } from '@/features/shared/constants/analytics'
import { useSearchQueryParams } from '@/features/shared/hooks/search'
import { useCMSTrackingEvents } from '@/features/cms/components/CMS/CMSContext'
import { useEffect, useMemo } from 'react'
import { normalizeProduct } from '@/features/shared/utils/normalizeProduct'
import { Carousel } from '@shipt/design-system-carousel'
import { useMediaQuery } from '@/features/shared/hooks/useMediaQuery'
import { ScrollIntoView } from '@/features/shared/components/ScrollIntoView'
import { getCMSWrapperProps } from '@/features/cms/components/CMS/utils/getCMSWrapperProps'
import { GridLayoutContainer } from '@/features/shared/styles/layout'
import { HERO_SHELF_ITEM_WIDTH } from '@/features/shared/constants/shelves'
import { loadBeaconsForAdType } from '@/features/cms/components/CMS/utils'
import { theme } from '@/features/shared/constants/shiptTheme'

type AdSkinnyShowcaseProps = AdShowcaseData & {
  index: number
}

export const AdSkinnyShowcase = ({
  data,
  metadata,
  name,
  index,
  analytic_slug,
  content_type_id,
  id,
}: AdSkinnyShowcaseProps) => {
  const { heading, web_image: webImage, link, items: adShowcaseItems } = data ?? {}
  const { read_operation_info } = metadata
  const {
    featuredTransactionID,
    view_beacon,
    click_beacon,
    row_position,
    featured_source,
    banner_type,
    aggregate_onLoadBeacons,
    load_beacon,
    ad_placement_id,
  } = read_operation_info ?? {}
  const { trackCMSPromoCardViewed, trackCMSPromoCardClicked } = useCMSTrackingEvents()
  const { screenMd: isDesktop } = useMediaQuery()

  const imgSrc = webImage?.src
  const destinationUrl = link?.url
  const { query = '', searchType } = useSearchQueryParams()
  const searchQuery = Array.isArray(query) ? '' : query
  const type = Array.isArray(searchType) ? '' : (searchType ?? '')
  const title = name || ''

  const normalizedProducts = useMemo(() => {
    return adShowcaseItems?.map((item) => normalizeProduct(item)) ?? []
  }, [adShowcaseItems])

  useEffect(() => {
    loadBeaconsForAdType({
      aggregate_onLoadBeacons,
      load_beacon,
      featuredTransactionID,
      ad_placement_id,
    })
  }, [ad_placement_id, aggregate_onLoadBeacons, load_beacon, featuredTransactionID])

  const eventProperties = {
    action_type: 'shop',
    creative: imgSrc,
    display_format: DISPLAY_FORMAT.SHELF,
    display_sublocation: analytic_slug,
    shelf_index: row_position ?? index,
    grid_index: index,
    next_location: destinationUrl,
    source: featured_source,
    promotion_type_name: banner_type ?? '',
    promotion_type_id: -1,
    search_term: searchQuery,
    promotion_name: name,
    promotion_category_id: parsePromotionCategoryId(destinationUrl ?? ''),
    ad_placement_id,
  }

  const handlePromoCardInView = () => {
    trackCMSPromoCardViewed({ ...eventProperties, beacon_url: view_beacon })
  }

  const handleAdHeadlineClick = () => {
    trackCMSPromoCardClicked({ ...eventProperties, beacon_url: click_beacon })
  }

  const shelfProducts = useRenderShelfProducts({
    shelfName: name,
    shelfIndex: index,
    isSponsoredShelf: true,
    products: normalizedProducts,
    wrapper: ProductCardWrapper,
    landscape: true,
    trackingProps: {
      searchTerm: searchQuery,
      searchType: type,
      featuredTransactionID,
      adPlacementId: ad_placement_id,
    },
    linkToUniversalPDP: false,
  })

  const adCard = (
    <AdCardWrapper>
      <ImageBox>
        {imgSrc ? <AdImage src={imgSrc} alt={webImage.alt} fill unoptimized /> : null}
      </ImageBox>
      <Body size="sm">Sponsored</Body>
    </AdCardWrapper>
  )

  if (!data) {
    return null
  }
  return (
    <ScrollIntoView onInView={handlePromoCardInView}>
      <ShowcaseLayoutContainer contentMaxWidth>
        <Container
          padding={{ base: ['xs', 0], md: ['sm', 'lg'] }}
          spacing="xs"
          marginBottom={{ md: 'lg' }}
          {...getCMSWrapperProps({ content_type_id, id })}
        >
          <HeadlineContainer>
            <HeadlineLink
              href={destinationUrl ?? '#'}
              onClick={handleAdHeadlineClick}
              aria-label={heading}
            >
              <HeadlineText size="sm">{heading}</HeadlineText>
              <HeadlineIconButton
                variant="tertiary"
                icon={ArrowRightIcon}
                size="sm"
                forwardedAs="span"
              />
            </HeadlineLink>
          </HeadlineContainer>
          {isDesktop ? (
            <Row>
              {adCard}
              <ShelfCarousel
                key="desktop"
                hasInlineControls
                title={{ content: title, isHidden: true }}
                layoutConfig={{
                  [0]: {
                    // @ts-expect-error `auto` is valid for Swiper but not exposed in the DS carousel layout config type.
                    slidesPerView: 'auto',
                    slidesPerGroup: 1,
                    slidesPerGroupAuto: true,
                    spaceBetween: 0,
                  },
                }}
              >
                {shelfProducts}
              </ShelfCarousel>
            </Row>
          ) : (
            <Row>
              <ShelfCarousel
                key="mobile"
                hasInlineControls
                title={{ content: title, isHidden: true }}
                layoutConfig={{
                  [0]: {
                    // @ts-expect-error `auto` is valid for Swiper but not exposed in the DS carousel layout config type.
                    slidesPerView: 'auto',
                    slidesPerGroup: 1,
                    slidesPerGroupAuto: true,
                    spaceBetween: 8,
                  },
                }}
              >
                {adCard}
                {shelfProducts}
              </ShelfCarousel>
            </Row>
          )}
        </Container>
      </ShowcaseLayoutContainer>
    </ScrollIntoView>
  )
}

const ShowcaseLayoutContainer = styled(GridLayoutContainer)`
  @media ${screenSizes.max_mobile} {
    --padding-inline: 0px;
  }

  border: solid ${theme.color.border.base.neutral.soft};
  border-width: 1px 0;

  @media ${screenSizes.tablet} {
    border: none;
  }
`

const Container = styled(Column)`
  @media ${screenSizes.tablet} {
    border: solid ${theme.color.border.base.neutral.soft};
    border-width: 1px 0;
  }
`

const HeadlineText = styled(Headline)`
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;

  @media ${screenSizes.max_mobile} {
    margin-left: 1rem;
  }
`

const HeadlineIconButton = styled(IconButton)`
  flex-shrink: 0;
`

const HeadlineContainer = styled.div`
  display: flex;
  justify-content: flex-start;
`

const HeadlineLink = styled(NextLink)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: fit-content;
`

const AdCardWrapper = styled.div`
  width: 11.5625rem;
  min-width: 11.5625rem;
`

const ImageBox = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 8px;
  height: 6.5rem;
  width: 100%;
  border: solid 1px hsl(0deg 0% 0% / 10%);
`

const AdImage = styled(Image)`
  object-fit: cover;
`

const ProductCardWrapper = styled.div`
  position: relative;
  width: ${HERO_SHELF_ITEM_WIDTH}px;
`

const ShelfCarousel = styled(Carousel)`
  flex: 1 1 auto;
  min-width: 0;
  overflow: visible;

  .swiper-slide {
    width: auto;
  }
`
